import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Tab, Tabs } from '@mui/material';
import { useBooleanFlagValue } from '@openfeature/react-sdk';

import { useBulkDownloadAndZip } from '../../hooks';
import { FHButton, FHConfirmDialog, FProgressBar } from '../../components/base';
import { FORTA_BLUE, FORTA_GRAY3, FORTA_WHITE } from '../../utils/colors';
import { claimsActions } from '../../store';
import PageContainer from '../../components/layout/PageContainer';
import ClaimPayments from '../../components/layout/billings/claim-info/claim-payments/ClaimPayments';
import Claim1500FormInfo from '../../components/layout/billings/claim-info/Claim1500FormInfo';
import ClaimServiceLineDetails from '../../components/layout/billings/claim-info/ClaimServiceLineDetails';
import ClaimOverview from '../../components/layout/billings/claim-info/claim-overview/ClaimOverview';
import SessionsInClaim from '../../components/layout/billings/claim-info/SessionsInClaim';
import ClaimInfoChips from '../../components/layout/billings/claim-info/ClaimInfoChips';
import { ClaimStatus } from '../../components/layout/billings/claim-info/constants';
import { areUserRolesIn, capitalizeFirstLetter, formatEntityId } from '../../helpers';
import { ENTITY_PREFIX, Roles } from '../../constants';
import { FeatureFlags } from '../../constants/featureFlags';
import DownloadIcon from '../../components/svgs/DownloanIcon';

import type { TabPanelProps } from '../../utils/types';
import type { RootState } from '../../store';

const fieldContainerStyle = {
  mb: 3,
};

const fieldLabelStyle = {
  fontWeight: 700,
  textAlign: 'left',
};

const fieldContentStyle = {
  textAlign: 'left',
  width: 'fit-content',
};

//Tab panel component
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      sx={{ mt: 4 }}
    >
      {value === index && <Box sx={{ p: 0, position: 'relative' }}>{children}</Box>}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function ClaimInfo() {
  const { claimId } = useParams();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const [tab, setTab] = useState<number>(0);
  const [showTebraDialog, setShowTebraDialog] = useState<boolean>(false);
  const [tebraSubmitting, setTebraSubmitting] = useState<boolean>(false);
  const { user } = useSelector((state: RootState) => state.auth);
  const {
    current: claim,
    fileUrls,
    loading: claimLoading,
  } = useSelector((state: RootState) => state.claims);
  const canSubmitToTebra = areUserRolesIn(user?.roles, [Roles.Admin, Roles.Biller]);
  const tebraEnabled = useBooleanFlagValue(FeatureFlags.TEBRA_ENABLED, false);

  useBulkDownloadAndZip(
    fileUrls,
    `${formatEntityId(String(claimId), ENTITY_PREFIX.Claim)}_PDFs`,
    true,
  );

  useEffect(() => {
    return () => {
      dispatch(claimsActions.updateState({ key: 'current', data: null }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!claimId) return;

    dispatch(claimsActions.getClaim({ claimId, getAccessTokenSilently }));
    return () => {
      dispatch(claimsActions.updateState({ key: 'fileUrls', data: [] }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const handleFetchDocumentLinks = () => {
    dispatch(claimsActions.getClaimDocumentUrls({ getAccessTokenSilently, claimId }));
  };

  const handleSubmitToTebra = async () => {
    setTebraSubmitting(true);
    try {
      const response: any = await dispatch(
        claimsActions.submitClaimToTebra({
          getAccessTokenSilently,
          claimId: claim?.id,
        }),
      );
      if (!response.error && claim?.id) {
        dispatch(
          claimsActions.getClaim({
            getAccessTokenSilently,
            claimId: claim.id,
          }),
        );
      }
    } finally {
      setTebraSubmitting(false);
      setShowTebraDialog(false);
    }
  };

  const showTebraButton =
    claim?.status === ClaimStatus.GENERATED && canSubmitToTebra && tebraEnabled;

  return (
    <PageContainer sx={{ pt: 2, px: 3 }} dataTestId="claimInfoContainer">
      {claimLoading && <FProgressBar />}
      <ClaimInfoChips claim={claim} showDeleteButton={areUserRolesIn(user?.roles, [Roles.Admin])} />
      <Box display={'flex'} flexDirection={'column'}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Tabs
            value={tab}
            onChange={handleChange}
            aria-label="basic tabs example"
            sx={{
              minHeight: 32,
              '& .MuiTab-root': {
                padding: '0 0.5rem',
                textTransform: 'none',
                minHeight: 32,
              },
            }}
          >
            <Tab
              label="Overview"
              {...a11yProps(0)}
              sx={{
                fontWeight: tab !== 0 ? 400 : 700,
                color: tab !== 0 ? `${FORTA_GRAY3} !important` : FORTA_BLUE.primary,
              }}
              data-testid="claimOverviewDetailsTab"
            />
            <Tab
              label="1500 form"
              {...a11yProps(1)}
              sx={{
                fontWeight: tab !== 1 ? 400 : 700,
                color: tab !== 1 ? `${FORTA_GRAY3} !important` : FORTA_BLUE.primary,
              }}
              data-testid="claimForm1500Tab"
            />
            <Tab
              label="Claim details & Service line details"
              {...a11yProps(2)}
              sx={{
                fontWeight: tab !== 2 ? 400 : 700,
                color: tab !== 2 ? `${FORTA_GRAY3} !important` : FORTA_BLUE.primary,
              }}
              data-testid="claimDetailsAndServicelineDetailsTab"
            />
            <Tab
              label="Payment"
              {...a11yProps(3)}
              sx={{
                fontWeight: tab !== 3 ? 400 : 700,
                color: tab !== 3 ? `${FORTA_GRAY3} !important` : FORTA_BLUE.primary,
              }}
              data-testid="claimPaymentTab"
            />
            <Tab
              label={`Sessions in the claim ${
                claim?.bay ? `(${capitalizeFirstLetter(claim?.bay?.toLowerCase() ?? '')})` : ''
              }`}
              {...a11yProps(4)}
              sx={{
                fontWeight: tab !== 4 ? 400 : 700,
                color: tab !== 4 ? `${FORTA_GRAY3} !important` : FORTA_BLUE.primary,
              }}
              data-testid="sessionsInClaim"
            />
          </Tabs>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {showTebraButton && (
              <FHButton
                data-testid="submitToTebraButton"
                sx={{ width: '160px' }}
                loading={tebraSubmitting}
                disabled={claimLoading}
                onClick={() => setShowTebraDialog(true)}
              >
                Submit to Tebra
              </FHButton>
            )}
            <FHButton
              sx={{
                width: '100px',
                backgroundColor: FORTA_WHITE,
                border: `1px solid ${FORTA_BLUE.light2}`,
                color: FORTA_BLUE.primary,
                '&:hover': {
                  backgroundColor: FORTA_WHITE,
                  color: FORTA_BLUE.primary,
                  border: `1px solid ${FORTA_BLUE.light2}`,
                },
              }}
              startIcon={<DownloadIcon color={FORTA_BLUE.primary} />}
              onClick={handleFetchDocumentLinks}
            >
              PDFs
            </FHButton>
          </Box>
        </Box>

        <TabPanel value={tab} index={0}>
          <ClaimOverview />
        </TabPanel>
        <TabPanel value={tab} index={1}>
          <Claim1500FormInfo
            fieldContainerStyle={fieldContainerStyle}
            fieldLabelStyle={fieldLabelStyle}
            fieldContentStyle={{ ...fieldContentStyle, ml: '34px' }}
          />
        </TabPanel>
        <TabPanel value={tab} index={2}>
          <ClaimServiceLineDetails
            fieldContainerStyle={fieldContainerStyle}
            fieldContentStyle={fieldContentStyle}
            fieldLabelStyle={fieldLabelStyle}
          />
        </TabPanel>
        <TabPanel value={tab} index={3}>
          <ClaimPayments />
        </TabPanel>
        <TabPanel value={tab} index={4}>
          <SessionsInClaim />
        </TabPanel>
      </Box>
      <FHConfirmDialog
        title="Submit claim to Tebra"
        open={showTebraDialog}
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
          },
        }}
        submittext="Submit"
        canceltext="Cancel"
        content={`Submit claim ${formatEntityId(claim?.id ?? '', ENTITY_PREFIX.Claim)} to Tebra? This creates a Draft encounter in Tebra PM for biller approval.`}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          setShowTebraDialog(false);
        }}
        onSubmit={handleSubmitToTebra}
      />
    </PageContainer>
  );
}

export default ClaimInfo;
